/**
 * @file NewPlanCreationSheet.jsx
 * @module ui/src/components/admin/NewPlanCreationSheet
 * @description
 *   Single plan-creation entry surface for CA-1. Offers three creation paths:
 *   CRL-recommended spine (ep 16), certified plan template from library (ep 28),
 *   and blank custom plan (ep 7). Each path resolves to POST /api/v1/plans (ep 7)
 *   after collecting all required identity fields in the 'identity' step.
 *
 * @version 1.3.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S807
 *
 * @architecture
 *   Locked decisions : #15
 *   Design principles : DP-1
 *
 * @wiring-s777
 *   ep 7  POST /api/v1/plans — WIRED. Required: planCode, planName, fiscalPeriodId,
 *         roleId, segmentId, primarySpine. Optional: businessUnitId, crlRecommendationId,
 *         description. Response: {planId, planCode, planStatus, version, createdAt}. Status 201.
 *   ep 16 GET /api/v1/crl/recommendation — PARAM FIXED S777: requires plan_id (int).
 *         OI-S774-CRL-PARAM-MISMATCH closed S777. OI-S777-CRL-PRE-CREATION-GAP CLOSED S795.
 *         Fix: CRL fetch fires immediately after POST /plans succeeds using returned planId.
 *   ep 28 GET /api/v1/template-library/templates/{id}/seed — outside v4 ep 1–61 scope.
 *
 * @migration-flags
 *   - OI-S774-MISSING-FILE-HEADERS — CLOSED S777.
 *   - OI-S774-CRL-PARAM-MISMATCH   — CLOSED S777.
 *   - OI-S777-CRL-PRE-CREATION-GAP — CLOSED S795.
 *   - OI-S807-NEWPLAN-422           — CLOSED S807. Identity step added. All 5 required
 *       fields now collected before POST: planCode, planName, roleId, segmentId, primarySpine.
 *       roles + segments received as props from PlanRegistry (no duplicate fetch).
 *       primarySpine selector with 6 canonical values. planCode auto-slugged from planName,
 *       user-editable. Full field-level validation before submit.
 *
 * @changelog
 *   1.0.0  S756  Initial build
 *   1.1.0  S777  Added JSDoc header. Fixed ep 16 param. Remapped CRL response fields. CRLF→LF.
 *   1.2.0  S795  OI-S777-CRL-PRE-CREATION-GAP: fire CRL fetch post-creation using returned planId.
 *   1.3.0  S807  OI-S807-NEWPLAN-422: insert 'identity' mode. Collect all 5 required fields.
 *                roles + segments as props. primarySpine selector. planCode auto-slug. Validation.
 */

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * @typedef {{
 *   templateId: number,
 *   templateCode: string,
 *   templateName: string,
 *   spineLabel: string,
 *   roleLabel: string | null,
 *   segmentLabel: string | null,
 *   confidence: 'high' | 'partial',
 * }} CRLRecommendation
 */

/**
 * @typedef {{ id: number, name: string }} RoleOption
 */

/**
 * @typedef {{ id: number, name: string }} SegmentOption
 */

// ---------------------------------------------------------------------------
// Canonical primarySpine values.
// DDL: plan.primary_spine varchar(50) — no CHECK constraint.
// Values derived from engine payout columns (base_component_stage) and
// router narrative (plan_governance_router.py line 4919).
// No application-layer validator in PlanCreateRequest — bare str field.
// ---------------------------------------------------------------------------
// Canonical spine keys — must match EarningsModelSelector.jsx SPINES array exactly.
// primary_spine is governance metadata only; the engine uses arr_weight/mbo_weight etc.
// on plan_component, not this field. These keys are the new-arch UI standard.
const SPINE_OPTIONS = [
  { value: 'revenue_bookings',       label: 'Revenue / Bookings',        description: 'Comp earned as a percentage of quota attainment — revenue, ARR, ACV, TCV, or bookings.' },
  { value: 'transaction_commission', label: 'Transaction Commission',     description: 'Flat rate or percentage paid per deal or unit sold. No quota required.' },
  { value: 'volume_quantity',        label: 'Volume / Quantity',          description: 'Comp earned based on total units or volume against a target.' },
  { value: 'market_share',           label: 'Market Share / Penetration', description: 'Comp earned based on share of the addressable market versus a target share.' },
  { value: 'grr_nrr_retention',      label: 'Retention (GRR / NRR)',      description: 'Comp earned based on gross or net revenue retained versus a retention target.' },
  { value: 'mbo_objective',          label: 'MBO / Objective',            description: 'Comp earned based on achievement of defined milestones, scores, or objectives.' },
  { value: 'margin_profit',          label: 'Margin / Profit',            description: 'Comp earned based on gross margin percentage or dollar margin versus target.' },
  { value: 'bonus_pool',             label: 'Bonus Pool / Allocation',    description: 'Comp allocated from a shared pool based on contribution or ranking.' },
  { value: 'pharma_script',          label: 'Pharma / Script Volume',     description: 'Comp earned based on prescription script volume versus target.' },
];

function extractPlanId(payload) {
  return payload?.planId ?? payload?.plan_id ?? null;
}

/**
 * Slugify planName → planCode suggestion.
 * Uppercase, replace spaces/special chars with underscores, max 40 chars.
 * Stays within plan.plan_code varchar(120) NN constraint.
 */
function slugifyPlanName(name) {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

const EMPTY_IDENTITY = {
  planName: '',
  planCode: '',
  roleId: '',
  segmentId: '',
  primarySpine: '',
  description: '',
};

const EMPTY_ERRORS = {
  planName: '',
  planCode: '',
  roleId: '',
  segmentId: '',
  primarySpine: '',
};

/**
 * Single plan-creation entry surface.
 *
 * Mode state machine:
 *   'options'  — choose creation path (custom / certified / recommended)
 *   'identity' — collect required identity fields, then POST /api/v1/plans
 *
 * Note: 'library' mode removed S875. "Browse Library →" now navigates to the
 * standalone /admin/plans/library route (PlanLibraryBrowser.jsx).
 *
 * @param {{
 *   open: boolean,
 *   fiscalPeriodId: number,
 *   currentUser: { user_account_id: string },
 *   roles: RoleOption[],
 *   segments: SegmentOption[],
 *   onCreated: (planId: number) => void,
 *   onCancel: () => void,
 *   className?: string
 * }} props
 */
export default function NewPlanCreationSheet({
  open,
  fiscalPeriodId,
  onCreated,
  onCancel,
  roles = [],
  segments = [],
  className = '',
}) {
  // ── ALL HOOKS BEFORE ANY EARLY RETURN (AH-HOOKS) ──────────────────────────
  const [recommendation, setRecommendation] = useState(null);
  const [recommendationLoading, setRecommendationLoading] =
    useState(false);

  const navigate = useNavigate();
  const [mode, setMode] = useState('options');
  // creationIntent captures what triggered the identity step:
  //   { type: 'custom' }
  //   { type: 'template', templateId: number, spineLabel: string }
  //   { type: 'recommended', recommendationId: string, recommendedSpine: string }
  const [creationIntent, setCreationIntent] = useState(null);

  const [creatingKey, setCreatingKey] = useState(null);
  const [error, setError] = useState(null);

  // Identity step state
  const [identityFields, setIdentityFields] =
    useState(EMPTY_IDENTITY);
  const [identityErrors, setIdentityErrors] =
    useState(EMPTY_ERRORS);
  // Whether planCode has been manually edited (stops auto-slug override)
  const [planCodeManuallyEdited, setPlanCodeManuallyEdited] =
    useState(false);

  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const recommendationAbortRef = useRef(null);
  const createAbortRef = useRef(null);

  // ── Focus trap + keyboard handler ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;

    const timer = window.setTimeout(() => {
      const focusTarget = dialogRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusTarget?.focus();
    }, 0);

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener(
        'keydown',
        handleKeyDown,
      );

      if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === 'function'
      ) {
        previousFocusRef.current.focus();
      }
    };
  }, [open, onCancel]);

  // ── Reset recommendation on close ─────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setRecommendation(null);
      setRecommendationLoading(false);
    }
  }, [open]);

  // OI-S777-CRL-PRE-CREATION-GAP — CLOSED S795.
  // WHY: ep 16 requires plan_id of an already-created plan. Fix: fire CRL
  // fetch immediately after POST /plans returns planId, while sheet is mounted.
  async function fetchCrlRecommendation(planId) {
    recommendationAbortRef.current?.abort();

    const controller = new AbortController();
    recommendationAbortRef.current = controller;

    setRecommendationLoading(true);

    try {
      const params = new URLSearchParams({ plan_id: String(planId) });
      const response = await fetch(
        `/api/v1/crl/recommendation?${params.toString()}`,
        { signal: controller.signal, credentials: 'include' },
      );

      if (!response.ok) return;

      const payload = await response.json();
      if (controller.signal.aborted) return;

      setRecommendation(payload);
    } catch {
      // AbortError expected on unmount/cancel. Other errors non-fatal.
    } finally {
      if (!controller.signal.aborted) {
        setRecommendationLoading(false);
      }
    }
  }

  // ── Full reset on close ────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setMode('options');
      setCreationIntent(null);
      setCreatingKey(null);
      setError(null);
      setIdentityFields(EMPTY_IDENTITY);
      setIdentityErrors(EMPTY_ERRORS);
      setPlanCodeManuallyEdited(false);
    }
  }, [open]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      recommendationAbortRef.current?.abort();
      createAbortRef.current?.abort();
    };
  }, []);

  // ── Derived values (must precede early return per AH-HOOKS) ───────────────
  // WHY: ep 16 router response fields — recommendedSpine, rationale, confidenceScore.
  const recommendationContext = recommendation
    ? [
        recommendation.recommendedSpine,
        recommendation.rationale,
      ].filter(Boolean)
    : [];

  // ── EARLY RETURN (all hooks above this line) ───────────────────────────────
  if (!open) return null;

  // ── Handlers (defined after early return — not hooks, safe) ───────────────

  // S875: Navigates to the standalone Plan Library Browser route.
  // The old inline mode:'library' render block has been removed.
  // PlanLibraryBrowser.jsx owns the browse + deploy flow at /admin/plans/library.
  function openTemplateBrowser() {
    navigate('/admin/plans/library');
  }

  // Enter identity step from any creation path
  function enterIdentityStep(intent) {
    // Pre-seed primarySpine from template or recommendation if available
    const preSeededSpine =
      intent.type === 'template'
        ? (SPINE_OPTIONS.find(
            (s) =>
              s.label.toLowerCase() ===
              (intent.spineLabel || '').toLowerCase(),
          )?.value ?? '')
        : intent.type === 'recommended'
        ? (SPINE_OPTIONS.find(
            (s) =>
              s.value === (intent.recommendedSpine || '').toLowerCase(),
          )?.value ?? '')
        : '';

    setCreationIntent(intent);
    setIdentityFields({
      ...EMPTY_IDENTITY,
      primarySpine: preSeededSpine,
    });
    setIdentityErrors(EMPTY_ERRORS);
    setPlanCodeManuallyEdited(false);
    setError(null);
    setMode('identity');
  }

  function handlePlanNameChange(value) {
    setIdentityFields((prev) => {
      const updated = { ...prev, planName: value };
      // Auto-slug planCode only if user has not manually edited it
      if (!planCodeManuallyEdited) {
        updated.planCode = slugifyPlanName(value);
      }
      return updated;
    });
    if (identityErrors.planName) {
      setIdentityErrors((prev) => ({ ...prev, planName: '' }));
    }
  }

  function handlePlanCodeChange(value) {
    setPlanCodeManuallyEdited(true);
    setIdentityFields((prev) => ({ ...prev, planCode: value.toUpperCase().replace(/[^A-Z0-9_\-]/g, '') }));
    if (identityErrors.planCode) {
      setIdentityErrors((prev) => ({ ...prev, planCode: '' }));
    }
  }

  function validateIdentity() {
    const errors = { ...EMPTY_ERRORS };
    let valid = true;

    if (!identityFields.planName.trim()) {
      errors.planName = 'Plan name is required.';
      valid = false;
    }
    if (!identityFields.planCode.trim()) {
      errors.planCode = 'Plan code is required.';
      valid = false;
    } else if (identityFields.planCode.length < 2) {
      errors.planCode = 'Plan code must be at least 2 characters.';
      valid = false;
    }
    if (!identityFields.roleId) {
      errors.roleId = 'Role is required.';
      valid = false;
    }
    if (!identityFields.segmentId) {
      errors.segmentId = 'Segment is required.';
      valid = false;
    }
    if (!identityFields.primarySpine) {
      errors.primarySpine = 'Earnings spine is required.';
      valid = false;
    }

    setIdentityErrors(errors);
    return valid;
  }

  async function submitIdentityAndCreate() {
    if (creatingKey) return;
    if (!validateIdentity()) return;

    createAbortRef.current?.abort();

    const controller = new AbortController();
    createAbortRef.current = controller;

    setCreatingKey('identity-submit');
    setError(null);

    try {
      const body = {
        planName: identityFields.planName.trim(),
        planCode: identityFields.planCode.trim(),
        fiscalPeriodId,
        roleId: Number(identityFields.roleId),
        segmentId: Number(identityFields.segmentId),
        primarySpine: identityFields.primarySpine,
        ...(identityFields.description.trim()
          ? { description: identityFields.description.trim() }
          : {}),
        ...(creationIntent?.type === 'template' &&
        creationIntent?.templateId != null
          ? { templateId: creationIntent.templateId }
          : {}),
        ...(creationIntent?.type === 'recommended' &&
        creationIntent?.recommendationId
          ? { crlRecommendationId: creationIntent.recommendationId }
          : {}),
      };

      const response = await fetch('/api/v1/plans', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let message = `Unable to create plan (${response.status}).`;

        try {
          const errorPayload = await response.json();
          message =
            errorPayload.detail ||
            errorPayload.message ||
            message;
        } catch {
          // Response had no JSON error body.
        }

        throw new Error(message);
      }

      const payload = await response.json();

      if (controller.signal.aborted) return;

      const planId = extractPlanId(payload);

      if (planId === null || planId === undefined) {
        throw new Error(
          'The plan was created but the response did not include a plan ID.',
        );
      }

      // OI-S777-CRL-PRE-CREATION-GAP CLOSED S795:
      // Fire CRL recommendation fetch now that planId is available.
      fetchCrlRecommendation(planId);

      onCreated(planId);
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setError(
          requestError.message || 'Unable to create plan.',
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setCreatingKey(null);
      }
    }
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgb(var(--color-bg)/0.7)] p-6"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !creatingKey
        ) {
          onCancel();
        }
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Create Plan"
        className={`max-h-[90vh] w-full max-w-[520px] overflow-hidden rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-xl ${className}`}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <header className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-[rgb(var(--color-text))]">
              {mode === 'identity' ? 'Plan Details' : 'Create Plan'}
            </h2>
            <p className="mt-0.5 text-[11px] text-[rgb(var(--color-text-muted))]">
              {mode === 'identity'
                ? 'Set the identity fields for the new plan.'
                : 'Create a governed plan document for this fiscal period.'}
            </p>
          </div>

          <button
            type="button"
            className="icon-button"
            aria-label="Close Create Plan"
            disabled={Boolean(creatingKey)}
            onClick={onCancel}
          >
            ×
          </button>
        </header>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="max-h-[calc(90vh-76px)] overflow-y-auto p-5">

          {/* ── MODE: options ─────────────────────────────────────────────── */}
          {mode === 'options' && (
            <div className="space-y-3">
              {recommendationLoading && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] p-4">
                  <div className="skeleton h-3 w-32 rounded" />
                  <div className="skeleton mt-3 h-4 w-52 rounded" />
                  <div className="skeleton mt-2 h-3 w-64 rounded" />
                  <div className="skeleton mt-4 h-8 w-36 rounded" />
                </div>
              )}

              {!recommendationLoading && recommendation && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Recommended spine: ${recommendation.recommendedSpine || 'See rationale'}`}
                  onClick={() => {
                    enterIdentityStep({
                      type: 'recommended',
                      recommendationId: recommendation.recommendationId,
                      recommendedSpine: recommendation.recommendedSpine,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      enterIdentityStep({
                        type: 'recommended',
                        recommendationId: recommendation.recommendationId,
                        recommendedSpine: recommendation.recommendedSpine,
                      });
                    }
                  }}
                  className="cursor-pointer rounded-xl border border-[rgb(var(--color-primary))] bg-[rgb(var(--color-selected-bg))] p-4"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-primary))]">
                    ★ Recommended for you
                  </div>

                  <div className="mt-2 text-[13px] font-semibold text-[rgb(var(--color-text))]">
                    {recommendation.recommendedSpine || 'CRL Recommendation'}
                  </div>

                  <div className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
                    {recommendationContext.join(' · ')}
                    {recommendation.confidenceScore !== undefined &&
                      recommendation.confidenceScore < 0.8 && (
                      <span className="text-[rgb(var(--color-warning))]">
                        {' '}· approximate match
                      </span>
                    )}
                  </div>

                  <p className="mt-3 text-[12px] text-[rgb(var(--color-text-muted))]">
                    Based on your role and segment context.
                  </p>

                  <div className="mt-4 text-right text-[12px] font-semibold text-[rgb(var(--color-primary))]">
                    Use Recommended →
                  </div>
                </div>
              )}

              {/* Certified plan option */}
              <div
                role="button"
                tabIndex={0}
                aria-label="Use a Certified Plan"
                onClick={openTemplateBrowser}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openTemplateBrowser();
                  }
                }}
                className="cursor-pointer rounded-xl border border-[rgb(var(--color-border))] p-4 hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <div className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                  Use a Certified Plan
                </div>
                <p className="mt-1 text-[12px] text-[rgb(var(--color-text-muted))]">
                  Browse approved plan templates from the library. Pre-configured and ready to configure.
                </p>
                <div className="mt-4 text-right text-[12px] font-semibold text-[rgb(var(--color-primary))]">
                  Browse Library →
                </div>
              </div>

              {/* Custom plan option */}
              <div
                role="button"
                tabIndex={0}
                aria-label="Build Custom Plan"
                onClick={() => {
                  enterIdentityStep({ type: 'custom' });
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    enterIdentityStep({ type: 'custom' });
                  }
                }}
                className="cursor-pointer rounded-xl border border-[rgb(var(--color-border))] p-4 hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <div className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                  Build Custom Plan
                </div>
                <p className="mt-1 text-[12px] text-[rgb(var(--color-text-muted))]">
                  Start from system defaults. Full configuration control.
                </p>
                <div className="mt-4 text-right text-[12px] font-semibold text-[rgb(var(--color-primary))]">
                  Start Building →
                </div>
              </div>
            </div>
          )}

          {/* ── MODE: identity ────────────────────────────────────────────── */}
          {mode === 'identity' && (
            <div>
              <button
                type="button"
                className="text-button mb-5"
                disabled={Boolean(creatingKey)}
                onClick={() => {
                  setMode('options');
                  setError(null);
                  setIdentityErrors(EMPTY_ERRORS);
                }}
              >
                ← Back to creation options
              </button>

              {/* Creation context banner */}
              {creationIntent?.type === 'template' && (
                <div className="mb-4 rounded-lg bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-[12px] text-[rgb(var(--color-text-muted))]">
                  <span className="font-semibold text-[rgb(var(--color-text))]">Starting from a certified template.</span>{' '}
                  Fill in the plan identity below.
                </div>
              )}
              {creationIntent?.type === 'recommended' && (
                <div className="mb-4 rounded-lg bg-[rgb(var(--color-selected-bg))] border border-[rgb(var(--color-primary))] px-4 py-3 text-[12px]">
                  <span className="font-semibold text-[rgb(var(--color-primary))]">★ CRL Recommended spine pre-selected.</span>{' '}
                  <span className="text-[rgb(var(--color-text-muted))]">Review and complete the fields below.</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Plan Name */}
                <div>
                  <label
                    htmlFor="npcs-plan-name"
                    className="block text-[12px] font-semibold text-[rgb(var(--color-text))]"
                  >
                    Plan Name <span className="text-[rgb(var(--color-error))]">*</span>
                  </label>
                  <input
                    id="npcs-plan-name"
                    type="text"
                    placeholder="e.g. Q1 FY2026 AE Enterprise ARR"
                    value={identityFields.planName}
                    disabled={Boolean(creatingKey)}
                    onChange={(e) => handlePlanNameChange(e.target.value)}
                    className={[
                      'mt-1 w-full rounded-md border px-3 py-2 text-[12px] text-[rgb(var(--color-text))]',
                      'bg-[rgb(var(--color-bg))] placeholder:text-[rgb(var(--color-placeholder))]',
                      identityErrors.planName
                        ? 'border-[rgb(var(--color-error))]'
                        : 'border-[rgb(var(--color-border))]',
                    ].join(' ')}
                    aria-describedby={identityErrors.planName ? 'npcs-plan-name-error' : undefined}
                  />
                  {identityErrors.planName && (
                    <p id="npcs-plan-name-error" className="mt-1 text-[11px] text-[rgb(var(--color-error))]">
                      {identityErrors.planName}
                    </p>
                  )}
                </div>

                {/* Plan Code */}
                <div>
                  <label
                    htmlFor="npcs-plan-code"
                    className="block text-[12px] font-semibold text-[rgb(var(--color-text))]"
                  >
                    Plan Code <span className="text-[rgb(var(--color-error))]">*</span>
                  </label>
                  <p className="mt-0.5 text-[11px] text-[rgb(var(--color-text-muted))]">
                    Unique identifier. Auto-suggested from plan name. Uppercase letters, numbers, underscores only.
                  </p>
                  <input
                    id="npcs-plan-code"
                    type="text"
                    placeholder="e.g. Q1_FY2026_AE_ENT_ARR"
                    value={identityFields.planCode}
                    disabled={Boolean(creatingKey)}
                    onChange={(e) => handlePlanCodeChange(e.target.value)}
                    className={[
                      'mt-1 w-full rounded-md border px-3 py-2 font-mono text-[12px] text-[rgb(var(--color-text))]',
                      'bg-[rgb(var(--color-bg))] placeholder:text-[rgb(var(--color-placeholder))]',
                      identityErrors.planCode
                        ? 'border-[rgb(var(--color-error))]'
                        : 'border-[rgb(var(--color-border))]',
                    ].join(' ')}
                    aria-describedby={identityErrors.planCode ? 'npcs-plan-code-error' : undefined}
                  />
                  {identityErrors.planCode && (
                    <p id="npcs-plan-code-error" className="mt-1 text-[11px] text-[rgb(var(--color-error))]">
                      {identityErrors.planCode}
                    </p>
                  )}
                </div>

                {/* Role + Segment row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="npcs-role"
                      className="block text-[12px] font-semibold text-[rgb(var(--color-text))]"
                    >
                      Role <span className="text-[rgb(var(--color-error))]">*</span>
                    </label>
                    <select
                      id="npcs-role"
                      value={identityFields.roleId}
                      disabled={Boolean(creatingKey)}
                      onChange={(e) => {
                        setIdentityFields((prev) => ({ ...prev, roleId: e.target.value }));
                        if (identityErrors.roleId) {
                          setIdentityErrors((prev) => ({ ...prev, roleId: '' }));
                        }
                      }}
                      className={[
                        'mt-1 w-full rounded-md border px-3 py-2 text-[12px] text-[rgb(var(--color-text))]',
                        'bg-[rgb(var(--color-bg))]',
                        identityErrors.roleId
                          ? 'border-[rgb(var(--color-error))]'
                          : 'border-[rgb(var(--color-border))]',
                      ].join(' ')}
                      aria-describedby={identityErrors.roleId ? 'npcs-role-error' : undefined}
                    >
                      <option value="">Select role…</option>
                      {roles.map((role) => (
                        <option key={role.id} value={String(role.id)}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    {identityErrors.roleId && (
                      <p id="npcs-role-error" className="mt-1 text-[11px] text-[rgb(var(--color-error))]">
                        {identityErrors.roleId}
                      </p>
                    )}
                    {roles.length === 0 && (
                      <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
                        No active roles found.
                      </p>
                    )}
                    {(() => {
                      if (!identityFields.roleId) return null;
                      const selectedRole = roles.find((r) => String(r.id) === String(identityFields.roleId));
                      const cat = selectedRole?.category ?? null;
                      if (!cat) return null;
                      const ENGINE_CONFIG = {
                        ic:     { label: 'Individual Contributor', desc: 'Earns from personal quota attainment only.', color: 'bg-blue-50 border-blue-200 text-blue-800', dot: 'bg-blue-500' },
                        hybrid: { label: 'Hybrid Manager',         desc: 'Earns from own quota attainment + team rollup. Own quota weight and team rollup weight must be configured in Performance & Rates.', color: 'bg-purple-50 border-purple-200 text-purple-800', dot: 'bg-purple-500' },
                        rollup: { label: 'Rollup Manager',         desc: 'Earns from team rollup only. No personal quota attainment.', color: 'bg-amber-50 border-amber-200 text-amber-800', dot: 'bg-amber-500' },
                      };
                      const cfg = ENGINE_CONFIG[cat];
                      if (!cfg) return (
                        <div className="mt-2 rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2">
                          <span className="text-[12px] font-semibold text-[rgb(var(--color-text-muted))]">Category: </span>
                          <span className="text-[12px] text-[rgb(var(--color-text))]">{cat}</span>
                          <p className="mt-0.5 text-[11px] text-[rgb(var(--color-text-muted))]">This category is not engine-calculated. Assign an ic, hybrid, or rollup role for payout calculation.</p>
                        </div>
                      );
                      return (
                        <div className={`mt-2 rounded-md border px-3 py-2.5 ${cfg.color}`}>
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                            <span className="text-[12px] font-bold">{cat}</span>
                            <span className="text-[12px] font-medium">— {cfg.label}</span>
                          </div>
                          <p className="mt-1 text-[12px] leading-snug opacity-90">{cfg.desc}</p>
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <label
                      htmlFor="npcs-segment"
                      className="block text-[12px] font-semibold text-[rgb(var(--color-text))]"
                    >
                      Segment <span className="text-[rgb(var(--color-error))]">*</span>
                    </label>
                    <select
                      id="npcs-segment"
                      value={identityFields.segmentId}
                      disabled={Boolean(creatingKey)}
                      onChange={(e) => {
                        setIdentityFields((prev) => ({ ...prev, segmentId: e.target.value }));
                        if (identityErrors.segmentId) {
                          setIdentityErrors((prev) => ({ ...prev, segmentId: '' }));
                        }
                      }}
                      className={[
                        'mt-1 w-full rounded-md border px-3 py-2 text-[12px] text-[rgb(var(--color-text))]',
                        'bg-[rgb(var(--color-bg))]',
                        identityErrors.segmentId
                          ? 'border-[rgb(var(--color-error))]'
                          : 'border-[rgb(var(--color-border))]',
                      ].join(' ')}
                      aria-describedby={identityErrors.segmentId ? 'npcs-segment-error' : undefined}
                    >
                      <option value="">Select segment…</option>
                      {segments.map((seg) => (
                        <option key={seg.id} value={String(seg.id)}>
                          {seg.name}
                        </option>
                      ))}
                    </select>
                    {identityErrors.segmentId && (
                      <p id="npcs-segment-error" className="mt-1 text-[11px] text-[rgb(var(--color-error))]">
                        {identityErrors.segmentId}
                      </p>
                    )}
                    {segments.length === 0 && (
                      <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
                        No active segments found.
                      </p>
                    )}
                  </div>
                </div>

                {/* Primary Spine */}
                <div>
                  <fieldset>
                    <legend className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
                      Earnings Spine <span className="text-[rgb(var(--color-error))]">*</span>
                    </legend>
                    <p className="mt-0.5 text-[11px] text-[rgb(var(--color-text-muted))]">
                      The primary measurement model for this plan (Layer 0).
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {SPINE_OPTIONS.map((spine) => {
                        const isSelected =
                          identityFields.primarySpine === spine.value;
                        return (
                          <button
                            key={spine.value}
                            type="button"
                            disabled={Boolean(creatingKey)}
                            onClick={() => {
                              setIdentityFields((prev) => ({
                                ...prev,
                                primarySpine: spine.value,
                              }));
                              if (identityErrors.primarySpine) {
                                setIdentityErrors((prev) => ({
                                  ...prev,
                                  primarySpine: '',
                                }));
                              }
                            }}
                            className={[
                              'rounded-lg border px-3 py-2.5 text-left transition-colors',
                              isSelected
                                ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-selected-bg))]'
                                : 'border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]',
                            ].join(' ')}
                            aria-pressed={isSelected}
                          >
                            <div className={[
                              'text-[12px] font-semibold',
                              isSelected
                                ? 'text-[rgb(var(--color-primary))]'
                                : 'text-[rgb(var(--color-text))]',
                            ].join(' ')}>
                              {spine.label}
                            </div>
                            <div className="mt-0.5 text-[11px] text-[rgb(var(--color-text-muted))]">
                              {spine.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {identityErrors.primarySpine && (
                      <p className="mt-2 text-[11px] text-[rgb(var(--color-error))]">
                        {identityErrors.primarySpine}
                      </p>
                    )}
                  </fieldset>
                </div>

                {/* Description (optional) */}
                <div>
                  <label
                    htmlFor="npcs-description"
                    className="block text-[12px] font-semibold text-[rgb(var(--color-text))]"
                  >
                    Description{' '}
                    <span className="text-[11px] font-normal text-[rgb(var(--color-text-muted))]">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id="npcs-description"
                    rows={2}
                    placeholder="Brief description of this plan's purpose or scope…"
                    value={identityFields.description}
                    disabled={Boolean(creatingKey)}
                    onChange={(e) =>
                      setIdentityFields((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="mt-1 w-full resize-none rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] px-3 py-2 text-[12px] text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-placeholder))]"
                  />
                </div>
              </div>

              {/* Submit button */}
              <div className="mt-6">
                <button
                  type="button"
                  className="primary-button w-full"
                  disabled={Boolean(creatingKey)}
                  onClick={submitIdentityAndCreate}
                >
                  {creatingKey === 'identity-submit'
                    ? 'Creating plan…'
                    : 'Create Plan →'}
                </button>

                {creatingKey === 'identity-submit' && (
                  <div
                    className="mt-3 h-1 overflow-hidden rounded-full bg-[rgb(var(--color-surface-muted))]"
                    aria-label="Creating plan"
                  >
                    <div className="skeleton h-full w-full" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Error banner (all modes) ───────────────────────────────────── */}
          {error && (
            <div
              role="alert"
              className="modifier-manager-inline-error mt-4"
            >
              {error}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
